import { ref } from 'vue';
import { FREE_BOOKMARK_LIMIT, IUserFsrsCardService, PaywallPopupViewModel } from '@lingvo-news/contracts';
import { getVueEnv } from '../infrastructure/vue-env';
import { isTelegramMiniApp } from '../infrastructure/auth/isTelegramMiniApp';

export type ToggleResult = 'saved' | 'removed' | 'noop';

export interface WordBookmarkOptions {
	/**
	 * When true, unknown word IDs are treated as already saved (for pages
	 * that list the user's own collection). Defaults to false.
	 */
	defaultSaved?: boolean;

	resolveUserId: () => Promise<string | null>;

	onUnauthenticated: () => Promise<void>;
}

const DEBOUNCE_MS = 400;

export function useWordBookmark(options: WordBookmarkOptions) {
	const savedState = ref<Map<string, boolean>>(new Map());
	const defaultSaved = options.defaultSaved ?? false;

	// Last state successfully confirmed by the server (used as rollback target)
	const confirmedState = new Map<string, boolean>();
	// Pending debounce timers — at most one per word
	const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();
	// Generation counter — incremented each time a request is sent; used to ignore stale responses
	const requestGen = new Map<string, number>();

	const isWordSaved = (wordId: string): boolean =>
		savedState.value.get(wordId) ?? defaultSaved;

	// Kept for API compatibility — no longer used to block clicks
	const isWordSaving = (_wordId: string): boolean => false;

	function setSaved(wordId: string, saved: boolean): void {
		const next = new Map(savedState.value);
		next.set(wordId, saved);
		savedState.value = next;
	}

	function markAllSaved(wordIds: string[]): void {
		const next = new Map(savedState.value);
		for (const id of wordIds) {
			if (!next.has(id)) {
				next.set(id, true);
				confirmedState.set(id, true);
			}
		}
		savedState.value = next;
	}

	function markSavedBulk(wordIds: Set<string>): void {
		savedState.value = new Map([...wordIds].map(id => [id, true]));
		for (const id of wordIds) {
			confirmedState.set(id, true);
		}
	}

	async function showBookmarkPaywall(): Promise<void> {
		const env = getVueEnv();
		await env.popupService.showPopup(new PaywallPopupViewModel({
			message: { stringId: 'errors.paywall.bookmarkLimitReached', formatParams: { limit: FREE_BOOKMARK_LIMIT } },
			buyMeCoffeeUrl: 'https://buymeacoffee.com/lingvo.news/membership',
			useTelegramPayment: isTelegramMiniApp(),
		}));
	}

	async function sendToServer(wordId: string, gen: number): Promise<void> {
		const desired = isWordSaved(wordId);
		const confirmed = confirmedState.get(wordId) ?? defaultSaved;

		if (desired === confirmed) return; // net no-op — toggles cancelled each other out

		const userId = await options.resolveUserId();
		if (!userId) {
			setSaved(wordId, confirmed);
			await options.onUnauthenticated();
			return;
		}

		// Bail if a newer request has already been dispatched
		if (requestGen.get(wordId) !== gen) return;

		const wordsService = getVueEnv().get(IUserFsrsCardService);

		try {
			if (desired) {
				const result = await wordsService.saveUserWord({ userId, translatedWordId: wordId });
				if (requestGen.get(wordId) !== gen) return; // superseded
				if (result.success) {
					confirmedState.set(wordId, true);
				} else {
					setSaved(wordId, confirmed); // rollback
					if (result.reason === 'bookmark_limit_reached') {
						await showBookmarkPaywall();
					}
				}
			} else {
				await wordsService.removeUserWord({ userId, translatedWordId: wordId });
				if (requestGen.get(wordId) !== gen) return; // superseded
				confirmedState.set(wordId, false);
			}
		} catch (error) {
			if (requestGen.get(wordId) !== gen) return; // superseded
			console.error('Failed to toggle word bookmark', error);
			setSaved(wordId, confirmed); // rollback
		}
	}

	function toggle(wordId: string): ToggleResult {
		// Optimistic: flip immediately
		const desired = !isWordSaved(wordId);
		setSaved(wordId, desired);

		// Cancel any pending timer for this word
		const existingTimer = pendingTimers.get(wordId);
		if (existingTimer !== undefined) clearTimeout(existingTimer);

		// Schedule server sync after debounce
		const timer = setTimeout(() => {
			pendingTimers.delete(wordId);
			const gen = (requestGen.get(wordId) ?? 0) + 1;
			requestGen.set(wordId, gen);
			void sendToServer(wordId, gen);
		}, DEBOUNCE_MS);

		pendingTimers.set(wordId, timer);

		return desired ? 'saved' : 'removed';
	}

	return {
		isWordSaved,
		isWordSaving,
		setSaved,
		markAllSaved,
		markSavedBulk,
		toggle,
	};
}
